import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { useAuth } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";
import AccountPageTemplate from "./AccountPageTemplate";

/** Serveur MCP distant (lot « utilisable par les IA ») — remplace @doodates/mcp-server, jamais publié. */
const MCP_SERVER_URL = "https://doodates.com/mcp";

type ApiKey = {
  id: string;
  name: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
};

type NewKeyResult = {
  id: string;
  plaintext: string;
};

export default function AccountApiKeys() {
  const { t, i18n } = useTranslation("common");
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<NewKeyResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedMcpUrl, setCopiedMcpUrl] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
  }, []);

  async function loadKeys() {
    setLoading(true);
    const { data, error } = await supabase
      .from("api_keys")
      .select("id, name, is_active, last_used_at, created_at")
      .order("created_at", { ascending: false });

    if (!error && data) setKeys(data);
    setLoading(false);
  }

  async function generateKey(): Promise<string> {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return (
      "ddk_" +
      Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  }

  async function hashKey(key: string): Promise<string> {
    const encoded = new TextEncoder().encode(key);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async function handleCreate() {
    if (!newKeyName.trim() || !user) return;
    setCreating(true);
    try {
      const plaintext = await generateKey();
      const keyHash = await hashKey(plaintext);

      const { data, error } = await supabase
        .from("api_keys")
        .insert({
          user_id: user.id,
          name: newKeyName.trim(),
          key_hash: keyHash,
        })
        .select("id")
        .single();

      if (error || !data) throw error;

      setNewKeyResult({ id: data.id, plaintext });
      setNewKeyName("");
      setShowForm(false);
      await loadKeys();
    } catch {
      // ignore — user sees no feedback, stays in form
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    setRevoking(id);
    await supabase.from("api_keys").update({ is_active: false }).eq("id", id);
    await loadKeys();
    setRevoking(null);
  }

  async function copyToClipboard(text: string) {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function copyMcpUrl() {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopiedMcpUrl(true);
      setTimeout(() => setCopiedMcpUrl(false), 2000);
    } catch {
      // Presse-papiers refusé : l'URL reste visible et sélectionnable.
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(i18n.language || "fr", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <AccountPageTemplate
      title={t("account.api_keys.title")}
      description={t("account.api_keys.description")}
      testId="account-api-keys-page"
    >
      <div className="space-y-6">
        {/* Clé nouvellement créée — à copier immédiatement */}
        {newKeyResult && (
          <Card className="border-success/40 bg-success-bg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-success text-base">
                <Key className="w-4 h-4" />
                {t("account.api_keys.new_key.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-success/80">
                {t("account.api_keys.new_key.warning")}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-background px-3 py-2 text-sm font-mono text-success break-all">
                  {showKey
                    ? newKeyResult.plaintext
                    : "•".repeat(newKeyResult.plaintext.length)}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowKey((v) => !v)}
                  data-testid="toggle-show-key"
                >
                  {showKey ? (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-success/60 text-success hover:bg-success-bg"
                  onClick={() => copyToClipboard(newKeyResult.plaintext)}
                  data-testid="copy-new-key-button"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {copied
                    ? t("account.api_keys.copied")
                    : t("account.api_keys.copy")}
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setNewKeyResult(null)}
                data-testid="dismiss-new-key"
              >
                {t("account.api_keys.new_key.dismiss")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Liste des clés */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              {t("account.api_keys.active_keys")}
            </CardTitle>
            {!showForm && (
              <Button
                size="sm"
                className="gap-1.5 bg-primary hover:bg-primary/90"
                onClick={() => setShowForm(true)}
                data-testid="create-api-key-button"
              >
                <Plus className="w-4 h-4" />
                {t("account.api_keys.new_key_button")}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Formulaire création */}
            {showForm && (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/60 p-3">
                <Input
                  placeholder={t("account.api_keys.name_placeholder")}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  className="flex-1 border-input bg-background text-foreground"
                  autoFocus
                  data-testid="new-key-name-input"
                />
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={creating || !newKeyName.trim()}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="confirm-create-key-button"
                >
                  {creating
                    ? t("account.api_keys.creating")
                    : t("account.api_keys.create")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowForm(false);
                    setNewKeyName("");
                  }}
                  data-testid="cancel-create-key-button"
                >
                  {t("account.api_keys.cancel")}
                </Button>
              </div>
            )}

            {/* Clés existantes */}
            {loading ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="keys-loading"
              >
                {t("account.api_keys.loading")}
              </p>
            ) : keys.filter((k) => k.is_active).length === 0 ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="no-keys-message"
              >
                {t("account.api_keys.empty")}
              </p>
            ) : (
              <div className="divide-y divide-border">
                {keys
                  .filter((k) => k.is_active)
                  .map((key) => (
                    <div
                      key={key.id}
                      className="flex items-center justify-between py-3"
                      data-testid={`api-key-row-${key.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {key.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("account.api_keys.created_on", {
                            date: formatDate(key.created_at),
                          })}
                          {key.last_used_at && (
                            <>
                              {" "}
                              {t("account.api_keys.last_used", {
                                date: formatDate(key.last_used_at),
                              })}
                            </>
                          )}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive/80 gap-1.5"
                        onClick={() => handleRevoke(key.id)}
                        disabled={revoking === key.id}
                        data-testid={`revoke-key-${key.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {revoking === key.id
                          ? t("account.api_keys.revoking")
                          : t("account.api_keys.revoke")}
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Utilisation depuis une IA : serveur MCP distant (plus de clé API) */}
        <Card data-testid="api-keys-mcp-usage">
          <CardHeader>
            <CardTitle className="text-sm text-foreground">
              {t("account.api_keys.usage.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>{t("account.api_keys.usage.connector_intro")}</p>
            <div className="flex items-center gap-2">
              <code
                className="min-w-0 flex-1 break-all rounded-lg bg-background p-3 text-xs text-foreground"
                data-testid="mcp-server-url"
              >
                {MCP_SERVER_URL}
              </code>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5"
                onClick={() => copyMcpUrl()}
                data-testid="copy-mcp-url"
              >
                {copiedMcpUrl ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {copiedMcpUrl
                  ? t("account.api_keys.copied")
                  : t("account.api_keys.copy")}
              </Button>
            </div>
            <p>{t("account.api_keys.usage.no_key_needed")}</p>
            <p>
              <a
                href="/ai"
                className="text-primary underline underline-offset-2 hover:text-primary/80"
                data-testid="ai-guide-link"
              >
                {t("account.api_keys.usage.guide_link")}
              </a>
            </p>
            <p className="text-xs">
              {t("account.api_keys.usage.keys_future_api")}
            </p>
          </CardContent>
        </Card>
      </div>
    </AccountPageTemplate>
  );
}
